import type { ReactNode } from "react";
import type { Pt, WallInput, WallResult } from "@tasduvar/engine";
import { backfillSurface, dimensionLines, fitScene, type Transform } from "../drawing/geometry";
import { Scene, SvgDefs } from "../drawing/SectionDrawing";
import { Dimensions } from "../drawing/layers/Dimensions";
import { Forces } from "../drawing/layers/Forces";
import { BasePressure } from "../drawing/layers/BasePressure";
import { PressureDiagram } from "../drawing/layers/PressureDiagram";
import { JointLevels } from "../drawing/layers/JointLevels";

/** Rapor çizimi: sabit 170 × 110 mm (643 × 416 px @ 96 dpi). */
export const REPORT_SVG = { width: 643, height: 416 };

export interface ReportDrawingProps {
  wall: WallInput; result: WallResult; forces?: boolean; caseId?: "static" | "seismicUp" | "seismicDown"; title: string;
  /** Ek katmanlar (ör. toptan göçme dairesi); verilirse ölçüler çizilmez */
  children?: (t: Transform) => ReactNode;
  /** Sahneye sığdırılacak ek noktalar */
  extraPoints?: Pt[];
}

export function ReportDrawing({ wall, result, forces = false, caseId = "static", title, children, extraPoints }: ReportDrawingProps): JSX.Element {
  const { box, t } = fitScene(result, wall, REPORT_SVG.width, REPORT_SVG.height, 28, extraPoints);
  const surf = backfillSurface(result, wall, box.maxX);
  const dims = dimensionLines(result, wall);
  const c = result.cases[caseId] ? caseId : "static";
  return (
    <figure>
      <svg role="img" aria-label={title} viewBox={`0 0 ${REPORT_SVG.width} ${REPORT_SVG.height}`} style={{ width: "170mm", height: "110mm", border: "1px solid #D1D5DB", background: "#fff" }}>
        <SvgDefs />
        <Scene t={t} box={box} surf={surf} farX={box.maxX} wall={wall} result={result} showWater />
        {forces ? (<>
          <PressureDiagram t={t} result={result} caseId={c} />
          <BasePressure t={t} result={result} caseId={c} />
          <JointLevels t={t} result={result} />
          <Forces t={t} result={result} caseId={c} />
        </>) : children ? children(t) : <Dimensions t={t} dims={dims} />}
      </svg>
      <figcaption>{title}</figcaption>
    </figure>
  );
}
